/**
 * DevisListView
 * List view for devis (quotes) with filters, pagination, and actions
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useDevisList, useDeleteDevis, useSendDevis } from '../../hooks/useDevis';
import { downloadDevisPdf } from '../../api/facturation.api';
import CreateDevisModal from './CreateDevisModal';
import ConvertDevisModal from './ConvertDevisModal';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { TableSkeletonLoader } from '../SkeletonLoader';
import type { Devis, DevisStatus, DevisFilters } from '../../types/facturation.types';
import { formatDateShort } from '../../utils/dateFormat';
import '../../facturation.css';

// Status configuration
const STATUS_CONFIG: Record<DevisStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Brouillon', className: 'draft' },
  SENT: { label: 'Envoye', className: 'sent' },
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

export const DevisListView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL search params
  const getInitialFiltersFromUrl = (): DevisFilters => {
    const status = searchParams.get('status') as DevisStatus | null;
    const page = searchParams.get('page');

    return {
      page: page ? parseInt(page) : 1,
      limit: 20,
      ...(status && { status }),
    };
  };

  // Filters state
  const [filters, setFilters] = useState<DevisFilters>(getInitialFiltersFromUrl());

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDevis, setEditingDevis] = useState<Devis | null>(null);
  const [convertingDevis, setConvertingDevis] = useState<Devis | null>(null);
  const [previewDevis, setPreviewDevis] = useState<Devis | null>(null);

  // Hooks
  const { data: devisData, isLoading, refetch } = useDevisList(filters);
  const deleteMutation = useDeleteDevis();
  const sendMutation = useSendDevis();

  // Update filters and sync with URL
  const updateFilters = (newFilters: Partial<DevisFilters>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);

    // Update URL params
    const params = new URLSearchParams();

    // Only add non-default values to URL
    if (updated.status) params.set('status', updated.status);
    if (updated.page && updated.page > 1) params.set('page', String(updated.page));

    setSearchParams(params, { replace: true });
  };

  const handleStatusFilter = (status?: DevisStatus) => {
    updateFilters({ status, page: 1 });
  };

  const handleDownloadPdf = async (devis: Devis, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await downloadDevisPdf(devis.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_${devis.devis_number.replace(/\//g, '-')}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100); // Delay for download to start
    } catch (error) {
      console.error('Download failed:', error);
      alert('Erreur lors du telechargement du PDF');
    }
  };

  const handleSend = async (devis: Devis, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Envoyer ce devis ? Un PDF sera genere et le devis ne pourra plus etre modifie.')) {
      try {
        await sendMutation.mutateAsync(devis.id);
        refetch();
      } catch (error) {
        console.error('Send failed:', error);
        alert('Erreur lors de l\'envoi du devis');
      }
    }
  };

  const handleDelete = async (devis: Devis, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Supprimer le devis ${devis.devis_number} ? Cette action est irreversible.`)) {
      try {
        await deleteMutation.mutateAsync(devis.id);
        refetch();
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleEdit = (devis: Devis, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDevis(devis);
    setShowCreateModal(true);
  };

  const handleConvert = (devis: Devis, e: React.MouseEvent) => {
    e.stopPropagation();
    setConvertingDevis(devis);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setEditingDevis(null);
    refetch();
  };

  const handleConvertSuccess = () => {
    setConvertingDevis(null);
    refetch();
  };

  const handleRowClick = (devis: Devis) => {
    // Only open preview if devis has a PDF (SENT status)
    if (devis.pdf_path) {
      setPreviewDevis(devis);
    }
  };

  const handlePreviewDownload = async () => {
    if (previewDevis) {
      try {
        const blob = await downloadDevisPdf(previewDevis.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devis_${previewDevis.devis_number.replace(/\//g, '-')}.pdf`;
        a.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 100); // Delay for download to start
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  return (
    <div className="fact-invoices-list">
      {/* Controls */}
      <div className="fact-controls">
        <div className="fact-controls-left">
          <div className="fact-status-filters">
            <button
              className={`fact-status-btn ${!filters.status ? 'active' : ''}`}
              onClick={() => handleStatusFilter(undefined)}
            >
              Tous
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'DRAFT' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('DRAFT')}
            >
              Brouillons
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'SENT' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('SENT')}
            >
              Envoyes
            </button>
          </div>
        </div>

        <div className="fact-controls-right">
          <button
            className="fact-btn fact-btn-primary"
            onClick={() => {
              setEditingDevis(null);
              setShowCreateModal(true);
            }}
          >
            <PlusIcon className="w-5 h-5" />
            <span>Nouveau devis</span>
          </button>
        </div>
      </div>

      {/* Devis List */}
      <div className="fact-list">
        {isLoading ? (
          <TableSkeletonLoader rows={5} columns={[100, 200, 100, 100, 100, 100, 80]} />
        ) : !devisData?.devis || devisData.devis.length === 0 ? (
          <div className="fact-empty">
            <DocumentTextIcon className="w-12 h-12" />
            <h3>Aucun devis</h3>
            <p>Aucun devis trouve. Cliquez sur "Nouveau devis" pour en creer un.</p>
          </div>
        ) : (
          <div className="fact-results-container">
            <div className="fact-table">
              <div className="fact-table-header">
                <div>Numero</div>
                <div>Contact</div>
                <div>Date</div>
                <div>Validite</div>
                <div style={{ textAlign: 'right' }}>Montant</div>
                <div>Statut</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>

              {devisData.devis.map((devis) => (
                <div
                  key={devis.id}
                  className={`fact-table-row ${devis.pdf_path ? 'clickable' : ''}`}
                  onClick={() => handleRowClick(devis)}
                >
                  <div>
                    <span className="fact-invoice-number">{devis.devis_number}</span>
                  </div>
                  <div className="fact-col-client">
                    <span className="fact-client-name">{devis.contact_name}</span>
                    {devis.contact_vat_number && (
                      <span className="fact-client-vat">{devis.contact_vat_number}</span>
                    )}
                    {devis.linked_affaire_id && (
                      <span className="text-xs text-green-600">
                        Converti: {devis.linked_affaire_id}
                      </span>
                    )}
                  </div>
                  <div className="fact-col-date">
                    <ClockIcon className="w-4 h-4" />
                    {formatDateShort(devis.devis_date)}
                  </div>
                  <div className="fact-col-date">
                    {devis.validity_date ? formatDateShort(devis.validity_date) : '-'}
                  </div>
                  <div className="fact-col-amount">
                    {formatCurrency(devis.total_tvac)}
                  </div>
                  <div>
                    <span className={`fact-status-badge ${STATUS_CONFIG[devis.status].className}`}>
                      {STATUS_CONFIG[devis.status].label}
                    </span>
                  </div>
                  <div className="fact-col-actions">
                    {/* Download PDF - only for SENT */}
                    {devis.status === 'SENT' && devis.pdf_path && (
                      <button
                        onClick={(e) => handleDownloadPdf(devis, e)}
                        className="fact-action-btn download"
                        title="Telecharger PDF"
                      >
                        <ArrowDownTrayIcon />
                      </button>
                    )}
                    {/* Edit - only for DRAFT */}
                    {devis.status === 'DRAFT' && (
                      <button
                        onClick={(e) => handleEdit(devis, e)}
                        className="fact-action-btn"
                        title="Modifier"
                      >
                        <PencilIcon />
                      </button>
                    )}
                    {/* Send - only for DRAFT */}
                    {devis.status === 'DRAFT' && (
                      <button
                        onClick={(e) => handleSend(devis, e)}
                        className="fact-action-btn finalize"
                        title="Envoyer (generer PDF)"
                      >
                        <PaperAirplaneIcon />
                      </button>
                    )}
                    {/* Convert - only for SENT and not yet converted */}
                    {devis.status === 'SENT' && !devis.linked_affaire_id && (
                      <button
                        onClick={(e) => handleConvert(devis, e)}
                        className="fact-action-btn validate"
                        title="Convertir en affaire"
                      >
                        <ArrowPathIcon />
                      </button>
                    )}
                    {/* Delete - only for DRAFT */}
                    {devis.status === 'DRAFT' && (
                      <button
                        onClick={(e) => handleDelete(devis, e)}
                        className="fact-action-btn cancel"
                        title="Supprimer"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {devisData.totalPages > 1 && (
              <div className="fact-pagination">
                <button
                  className="fact-page-btn"
                  disabled={devisData.page <= 1}
                  onClick={() => updateFilters({ page: (filters.page || 1) - 1 })}
                >
                  Precedent
                </button>
                <span className="fact-page-info">
                  Page {devisData.page} sur {devisData.totalPages}
                </span>
                <button
                  className="fact-page-btn"
                  disabled={devisData.page >= devisData.totalPages}
                  onClick={() => updateFilters({ page: (filters.page || 1) + 1 })}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateDevisModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingDevis(null);
        }}
        devis={editingDevis}
        onSuccess={handleCreateSuccess}
      />

      {convertingDevis && (
        <ConvertDevisModal
          isOpen={!!convertingDevis}
          onClose={() => setConvertingDevis(null)}
          devis={convertingDevis}
          onSuccess={handleConvertSuccess}
        />
      )}

      {/* PDF Preview Modal */}
      {previewDevis && (
        <PdfPreviewModal
          document={{
            type: 'devis',
            id: previewDevis.id,
            number: previewDevis.devis_number,
            date: previewDevis.devis_date,
            clientName: previewDevis.contact_name,
            amount: previewDevis.total_tvac,
            status: previewDevis.status,
            hasPdf: !!previewDevis.pdf_path,
          }}
          onClose={() => setPreviewDevis(null)}
          onDownload={handlePreviewDownload}
        />
      )}
    </div>
  );
};

export default DevisListView;
